import { Episode } from './Episode';
import { ThumbnailVariantsMap } from './ThumbnailVariant';

export enum EpisodeState {
  WIP = 'WIP',
  READY_TO_REVIEW = 'READY_TO_REVIEW',
  PUBLISHED = 'PUBLISHED',
  SCHEDULED = 'SCHEDULED',
  DELETED = 'DELETED',
  BANK = 'BANK',
}

export interface Show {
  id: string;
  name: string;
  creator: string;
  createdAt: Date;
  description: string;
  genre: string;
  trope: string;
  rating: number;
  state: ShowState;
  releasedAt: string | null;
  scheduledAt: string | null;
  updatedAt: Date;
  thumbNailUrl: string | null;
  category: ShowCategory;
  editorScore: number;
  createdBy: string | null;
  episodes: Episode[];
  type: ShowType;
  platformType: PlatformType;
  platformTypes?: PlatformType[];
  metadata: ShowMetaData[];
  episodeCount: number | null;
  tag: string;
  cluster: string;
  publisher: string;
  author: string;
  originCountry?: string | null;
  isOutsourced?: boolean;
  outsourcedShowId?: string | null;
  actorEthnicity?: string | null;
  leadCharacterGender?: string | null;
  targetAudienceGender?: string | null;
  maturityRating?: string | null;
  sourceLanguage?: string | null;
  subGenres?: string[];
  subTropes?: string[];
  tags?: string[];
  pushNotifications?: PushNotification[];
  thumbnailVariants?: ThumbnailVariantsMap;
  countryBlacklist?: string[]; // Array of country codes (e.g., ["US", "IN", "BR"])
  tenantReleases?: TenantRelease[];
}

export interface ShowStats {
  showId: string;
  likes: number;
  comments: number;
  views?: number;
}

export interface ShowWithViewsAndEpisodeCount {
  id: string;
  name: string;
  creator: string;
  description: string;
  genre: string;
  trope: string;
  thumbNailUrl?: string;
  views: number;
  episodeCount: number;
  metadata: ShowMetaData[];
}

export enum ShowCategory {
  UGC = 'UGC',
  PUGC = 'PUGC',
  PGC = 'PGC',
}

export enum ShowContentDerivative {
  ORIGINAL = 'ORIGINAL',
  REINTERPRETATION = 'REINTERPRETATION',
  PARODY = 'PARODY',
  FAN_ART = 'FAN_ART',
}

export enum StudioShowAspectRatioSettingEnum {
  DEFAULT = 'DEFAULT',
  ASPECT_RATIO_9_16 = 'ASPECT_RATIO_9_16',
  ASPECT_RATIO_5_4 = 'ASPECT_RATIO_5_4',
  ASPECT_RATIO_4_5 = 'ASPECT_RATIO_4_5',
  ASPECT_RATIO_2_1_2 = 'ASPECT_RATIO_2_1_2'
}

export enum MaturityRating {
  PG_13 = '13',
  RATED_16 = '16',
  RATED_R = '18',
}

export enum ShowStyle {
  REALISTIC_COMIC,
}

export enum ShowMetaDataType {
  DAILY_CHALLENGE,
  BANNER_COMIC,
  STAFF_PICKS,
  COMIC_STYLE = 'COMIC_STYLE',
  BANNER_THUMBNAIL_V2 = 'BANNER_THUMBNAIL_V2',
  WIDGET_THUMBNAIL_V2 = 'WIDGET_THUMBNAIL_V2',
  DETAIL_PAGE_THUMBNAIL_V2 = 'DETAIL_PAGE_THUMBNAIL_V2',
  CONTINUE_READING_THUMBNAIL_V2 = 'CONTINUE_READING_THUMBNAIL_V2',
  ENABLE_STYLE_WEIGHTS = 'ENABLE_STYLE_WEIGHTS',
  DEFAULT_STYLE_WEIGHT = 'DEFAULT_STYLE_WEIGHT',
  STYLE_NAME = 'STYLE_NAME',
  DEFAULT_CFG = 'DEFAULT_CFG',
  DEFAULT_WIDTH = 'DEFAULT_WIDTH',
  DEFAULT_HEIGHT = 'DEFAULT_HEIGHT',
  CONTENT_TYPE = 'CONTENT_TYPE',
  MAX_GENERATION_DIMENSION = 'MAX_GENERATION_DIMENSION',
  DEFAULT_GENERATION_STEPS = 'DEFAULT_GENERATION_STEPS',
  DEFAULT_CHARACTER_WEIGHT = 'DEFAULT_CHARACTER_WEIGHT',
  FAST_PASS_ENABLED = 'FAST_PASS_ENABLED',
  IS_TRIAL_SHOW = 'IS_TRIAL_SHOW',
  DAILY_FAST_PASS_EPISODE_COUNT = 'DAILY_FAST_PASS_EPISODE_COUNT',
  MATURITY_RATING = 'MATURITY_RATING',
  CONTENT_DERIVATIVE = 'CONTENT_DERIVATIVE',
  DEPICTION = 'DEPICTION',
  ALTERATION = 'ALTERATION',
  POST_CREDIT_THUMBNAILS = 'POST_CREDIT_THUMBNAILS',
  END_CREDIT_PANELS = 'END_CREDIT_PANELS',
  IS_VIDEO_COMIC = 'IS_VIDEO_COMIC',
  STUDIO_ASPECT_RATIO_SETTING = 'STUDIO_ASPECT_RATIO_SETTING',
  REELS_THUMBNAIL_4X5 = 'THUMBNAIL_4X5',
  REELS_THUMBNAIL_5x8 = 'THUMBNAIL_5X8',
  REELS_VIDEO_THUMBNAIL_9X16 = 'VIDEO_THUMBNAIL_9X16',
  REELS_THUMBNAIL_2x1_2 = 'THUMBNAIL_2X1_2',
  THUMBNAIL_24X7 = 'THUMBNAIL_24X7',
  SUBTITLE_DISABLED = 'SUBTITLE_DISABLED',
  LANGUAGE = 'LANGUAGE',
  SUBTITLE_LANGUAGE = 'SUBTITLE_LANGUAGE',
  REELS_SHOW_CLASSIFICATION = 'REELS_SHOW_CLASSIFICATION',
  ACTOR_ETHNICITY = 'ACTOR_ETHNICITY',
  SOURCE_SUBTITLE_LANGUAGE = 'SOURCE_SUBTITLE_LANGUAGE',
  TRAILER_LINKS = 'TRAILER_LINKS',
  PROMO_PERFORMANCE_DETAILS = 'PROMO_PERFORMANCE_DETAILS',
  SUCCESSFUL_PROMOS = 'SUCCESSFUL_PROMOS',
  LEAD_CHARACTER_GENDER = 'LEAD_CHARACTER_GENDER',
  TARGET_AUDIENCE_GENDER = 'TARGET_AUDIENCE_GENDER',
  SOURCE_LANGUAGE = 'SOURCE_LANGUAGE',
  TOP_PERFORMING_SHOW = 'TOP_PERFORMING_SHOW',
  VERNAUCLAR_NAME = 'VERNAUCLAR_NAME',
  ACTOR_GENDER = 'ACTOR_GENDER',
  IS_AI_SHOW = 'IS_AI_SHOW',
  CONTENT_ORIGIN = 'CONTENT_ORIGIN',
  VISUAL_STYLE = 'VISUAL_STYLE',
  SHOW_CONTENT_TYPE = 'SHOW_CONTENT_TYPE'
}
export interface ShowMetaData {
  id: string;
  showId: string;
  type: ShowMetaDataType;
  value: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ShowMetaDataDTO {
  type: ShowMetaDataType;
  value?: string;
  transcoded?: boolean[];
  sequence?: number[];
  label?: string; // Optional label for thumbnail variants (e.g., "Character Focus", "Action Scene")
}

export enum ShowType {
  ONE_SHOT = 'ONE_SHOT',
  SERIALIZED = 'SERIALIZED',
}

export enum ShowState {
  UNPUBLISHED = 'UNPUBLISHED',
  READY_TO_REVIEW = 'READY_TO_REVIEW',
  PUBLISHED = 'PUBLISHED',
  SCHEDULED = 'SCHEDULED',
  DELETED = 'DELETED',
}

export enum PlatformType{
  STUDIO ='STUDIO',
  DASHREEL= 'DASHREEL',
  SHORTFREE = 'SHORTFREE',
}

export enum ShowContentType {
  AUTO_DUBBED = 'AUTO_DUBBED',
  MANUAL_DUBBED = 'MANUAL_DUBBED',
  DIRECT_DISTRIBUTION = 'DIRECT_DISTRIBUTION',
  ORIGINAL = 'ORIGINAL'
}

export type DashTenant = 'REELS' | 'SHORTFREE';

// Device platform axis (ANDROID/IOS/WEB/TIKTOK_MINIS). Distinct from PlatformType,
// which despite its name is the tenant axis (DASHREEL/SHORTFREE).
export enum DevicePlatform {
  ANDROID = 'ANDROID',
  IOS = 'IOS',
  WEB = 'WEB',
  TIKTOK_MINIS = 'TIKTOK_MINIS',
}

export const ALL_DEVICE_PLATFORMS: DevicePlatform[] = [
  DevicePlatform.ANDROID,
  DevicePlatform.IOS,
  DevicePlatform.WEB,
  DevicePlatform.TIKTOK_MINIS,
];

export const devicePlatformDisplayName: Record<DevicePlatform, string> = {
  [DevicePlatform.ANDROID]: 'Android',
  [DevicePlatform.IOS]: 'iOS',
  [DevicePlatform.WEB]: 'Web',
  [DevicePlatform.TIKTOK_MINIS]: 'TikTok Minis',
};

export interface TenantRelease {
  tenant: DashTenant;
  // Absent on legacy rows written before the platform dimension shipped — a row
  // without platform applies to every platform of its tenant.
  platform?: DevicePlatform;
  state: ShowState;
  releasedAt: number | null;
  scheduledAt: number | null;
}

// Resolve the release row governing (tenant, platform): an explicit platform row
// wins over a legacy tenant-wide row.
export const findTenantPlatformRelease = (
  releases: TenantRelease[] | undefined,
  tenant: DashTenant,
  platform: DevicePlatform,
): TenantRelease | undefined => {
  const tenantRows = (releases || []).filter(tr => tr.tenant === tenant);
  return tenantRows.find(tr => tr.platform === platform) ?? tenantRows.find(tr => !tr.platform);
};

export const platformTypeToTenant: Record<string, DashTenant> = {
  [PlatformType.DASHREEL]: 'REELS',
  [PlatformType.SHORTFREE]: 'SHORTFREE',
};

export const tenantToPlatformType: Record<DashTenant, PlatformType> = {
  'REELS': PlatformType.DASHREEL,
  'SHORTFREE': PlatformType.SHORTFREE,
};

export const tenantDisplayName: Record<DashTenant, string> = {
  'REELS': 'DashReels',
  'SHORTFREE': 'ShortFree',
};
export interface ShowDTO {
  name: string;
  creator: string;
  thumbNailImageData: string | null;
  description: string;
  genre: string;
  trope: string;
  createdBy: string | null;
  platformType: PlatformType;
  platformTypes?: PlatformType[];
  metadata: ShowMetaDataDTO[];
  type: ShowType;
  tag: string;
  cluster: string;
  publisher: string;
  author: string;
  originCountry?: string | null;
}

export interface ShowUpdateDTO {
  id: string;
  thumbNailImageData: string | null;
}

export interface ShowDetailsRequest {
  requestedMetadata: string[];
  includeEpisodes?: boolean;
  includeEpisodeUserMapping?: boolean;
}

export interface ShowClassifiers {
  showIds: string[];
  genreList: string[];
  types: ShowType[];
  metadataList: ShowMetaDataDTO[];
  categories: ShowCategory[];
  creators: string[];
  states: ShowState[];
  sortByEditorScore: boolean;
  sortByCreatedAt: boolean;
  count: number;
  offset: number;
}

export interface AvailableRolesResponse {
  roles: string[];
}

export interface ShowStudioUser {
  name: string;
  email: string;
  role: string;
  shows: string[];
  photoUrl?: string | null;
}

export enum RBACRole {
  OWNER = 'OWNER',
  MANAGER = 'MANAGER',
  CREATOR = 'CREATOR',
  TRAINER = 'TRAINER',
}

export interface ShowStudioUsersResponse {
  users: ShowStudioUser[];
}

export interface RBACEntryCreationRequest {
  showId: string;
  users: RBACUserListGroup[];
}

export interface RBACUserListGroup {
  role: string;
  userIds: string[];
}

export interface RBACUserRemoveRequest {
  showId: string;
  userEmail: string;
  role: string;
}

export interface RBACUpdateRoleRequest {
  showId: string;
  userEmail: string;
  newRole: string;
}

export enum DashEntityType {
  SHOW = 'SHOW',
  EPISODE = 'EPISODE',
  PANEL = 'PANEL',
  CHARACTER = 'CHARACTER',
}

export enum ReactionType {
  LIKE = 'LIKE',
  DISLIKE = 'DISLIKE',
}

export enum LANG_CODES {
  ENGLISH = 'en',
  HINDI = 'hi',
  CHINESE = 'zh',
  MALAYALAM = 'ml',
  TAMIL = 'ta',
  TELUGU = 'te',
  GUJARATI = 'gu',
  MARATHI = 'mr',
  BENGALI = 'bn',
  KANNADA = 'kn',
  PUNJABI = 'pa',
  URDU = 'ur',
  ODIA = 'or',
  ASSAMESE = 'as',
  SPANISH = 'es',
  FRENCH = 'fr',
  GERMAN = 'de',
  JAPANESE = 'ja',
  KOREAN = 'ko',
  ARABIC = 'ar',
  PORTUGUESE = 'pt',
  RUSSIAN = 'ru',
  ITALIAN = 'it',
  INDONESIAN = 'id',
  MALAY = 'ms',
}

export const getLangCode = (lang: string): string => {
  switch (lang) {
    case 'en':
      return 'English';
    case 'hi':
      return 'Hindi';
    case 'zh':
      return 'Chinese';
    case 'ml':
      return 'Malayalam';
    case 'ta':
      return 'Tamil';
    case 'te':
      return 'Telugu';
    case 'gu':
      return 'Gujarati';
    case 'mr':
      return 'Marathi';
    case 'bn':
      return 'Bengali';
    case 'kn':
      return 'Kannada';
    case 'pa':
      return 'Punjabi';
    case 'ur':
      return 'Urdu';
    case 'or':
      return 'Odia';
    case 'as':
      return 'Assamese';
    case 'es':
      return 'Spanish';
    case 'fr':
      return 'French';
    case 'de':
      return 'German';
    case 'ja':
      return 'Japanese';
    case 'ko':
      return 'Korean';
    case 'ar':
      return 'Arabic';
    case 'pt':
      return 'Portuguese';
    case 'ru':
      return 'Russian';
    case 'it':
      return 'Italian';
    case 'id':
      return 'Indonesian';
    case 'ms':
      return 'Malay';
    default:
      return 'English';
  }
};

export enum ReelsShowClassification {
  FREE = 'FREE',
  PREMIUM = 'PREMIUM',
  GROWTH = 'GROWTH',
}

export interface AvailableSeasons {
  id: string;
  name: string;
  episodeCount?: number;
}

export interface PushNotification {
  id: string;
  showId: string;
  text: string;
  trope?: string;
  subTrope1?: string;
  subTrope2?: string;
  cluster?: string;
  tags: string[];
  tropeCategory?: string;
  studioUploadedThumbnail?: string;
  hindiThumbnailDriveLink?: string;
  targetAudience?: string;
  internalCopy: boolean;
  finalUploadAt?: Date;
}

// Paginated response interface to match backend structure
export interface PaginatedShowsResponse {
  data: Show[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

// CMS API response types
export interface Genre {
  id: string;
  name: string;
  description?: string;
}

export interface Tag {
  id: string;
  name: string;
  description?: string;
}

export interface Trope {
  id: string;
  name: string;
  description?: string;
}

export interface Cluster {
  id: string;
  name: string;
  description?: string;
}